import React, { useEffect, useRef, useState } from 'react';
import SuggestionActivity from './SuggestionActivity';
import ShortId from './ShortId';

// Everything we know about one member, opened from their card in
// Interconnecting's "Who are you emailing" — main profile, this community's
// profile answers, other communities, and anything they told the /meet AI
// chat (with the full chat on request).

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const show = (v) => (Array.isArray(v) ? v.join(', ') : (v === null || v === undefined ? '' : String(v)));

const Row = ({ label, value }) => {
  const text = show(value).trim();
  if (!text) return null;
  return (
    <div style={{ display: 'flex', gap: '0.5rem', margin: '0.15rem 0' }}>
      <span className="as-card-meta" style={{ minWidth: '8.5rem' }}>{label}</span>
      <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>
    </div>
  );
};

const MEET_ANSWER_LABELS = {
  firstName: 'Name given',
  aboutMe: 'About them',
  lookingFor: 'Looking for',
  age: 'Age',
  ageRange: 'Age range',
  gender: 'Gender',
  location: 'Based',
  personality: 'Clicks with',
};

const MeetChat = ({ chat, authedFetch }) => {
  const [transcript, setTranscript] = useState(null);
  const [error, setError] = useState('');
  const a = chat.answers || {};
  const pref = a.agePreference || {};

  const loadTranscript = async () => {
    setError('');
    try {
      const data = await authedFetch(`/admin/meet-analytics/conversations/${chat.sessionId}`);
      setTranscript(data.messages || []);
    } catch (err) {
      setError(`Couldn't load the chat (${err.message})`);
    }
  };

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.6rem 0.75rem', marginTop: '0.5rem' }}>
      <div className="as-card-meta" style={{ marginBottom: '0.3rem' }}>
        {fmtDate(chat.createdAt)} · {chat.communityName} · {chat.variant === 2 ? 'v2' : 'v1'} · {chat.status}{chat.utmSource ? ` · from ${chat.utmSource}` : ''}
      </div>
      {Object.entries(MEET_ANSWER_LABELS).map(([k, label]) => <Row key={k} label={label} value={a[k]} />)}
      {(pref.min || pref.max) && <Row label="Wants to meet ages" value={`${pref.min || '?'}–${pref.max || '?'}`} />}
      {Object.entries(a.profile || {}).map(([k, v]) => <Row key={`p-${k}`} label={k} value={v} />)}
      {!transcript && (
        <button type="button" className="as-text-link" onClick={loadTranscript}>Show the whole chat</button>
      )}
      {error && <div className="as-error">{error}</div>}
      {transcript && (
        <div style={{ marginTop: '0.4rem', maxHeight: '18rem', overflowY: 'auto', fontSize: '0.88rem' }}>
          {transcript.map((m, i) => (
            <div key={i} style={{ margin: '0.25rem 0', paddingLeft: m.role === 'user' ? '1rem' : 0 }}>
              <strong>{m.role === 'user' ? 'Them' : 'Bot'}:</strong> {m.content}
              {m.suggestions?.length > 0 && (
                <div className="as-card-meta">
                  {m.suggestions.map((p) => `${p.name}${p.ageRange ? ` (${p.ageRange})` : ''}`).join(' · ')}{m.examples ? ' — example profiles' : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Bot messages are stored as HTML (<strong>, <br>, entities) — shown here
// as plain text rather than injected.
const plainText = (html) => {
  const el = document.createElement('div');
  el.innerHTML = String(html || '').replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n');
  return (el.textContent || '').trim();
};

export const BotChat = ({ communityId, userId, authedFetch, scrollIntoView }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    authedFetch(`/admin/communities/${communityId}/members/${userId}/bot-chat`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(`Couldn't load the bot chat (${err.message})`); });
    return () => { cancelled = true; };
  }, [communityId, userId, authedFetch]);

  useEffect(() => {
    if (scrollIntoView && data) ref.current?.scrollIntoView({ block: 'start' });
  }, [scrollIntoView, data]);

  return (
    <div ref={ref}>
      <h4 style={{ margin: '0.9rem 0 0.3rem' }}>Community bot chat</h4>
      {error && <div className="as-error">{error}</div>}
      {!data && !error && <div className="as-card-meta">Loading…</div>}
      {data && !data.conversationFound && <div className="as-card-meta">No chat with the bot.</div>}
      {data?.conversationFound && data.messages.length === 0 && (
        <div className="as-card-meta">The chat was created {fmtDate(data.startedAt)} but has no messages — they were never welcomed.</div>
      )}
      {data?.messages.length > 0 && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.6rem 0.75rem', maxHeight: '22rem', overflowY: 'auto', fontSize: '0.88rem' }}>
          {data.messages.map((msg, i) => (
            <div key={i} style={{ margin: '0.35rem 0', paddingLeft: msg.fromMember ? '1rem' : 0 }}>
              <span className="as-card-meta">{fmtDate(msg.createdAt)}</span>{' '}
              <strong>{msg.fromMember ? 'Them' : 'Bot'}:</strong>{' '}
              <span style={{ whiteSpace: 'pre-wrap' }}>{plainText(msg.text)}</span>
              {msg.quickReplies.length > 0 && <div className="as-card-meta">Buttons: {msg.quickReplies.join(' · ')}</div>}
              {msg.suggestions?.length > 0 && (
                // The suggestion cards they were shown, at this point in the chat.
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0.35rem 0 0.2rem' }}>
                  {msg.suggestions.map((p) => (
                    <div key={p.userId} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.4rem 0.55rem', background: '#f8fafc', minWidth: 0, maxWidth: '100%' }}>
                      {p.pic
                        ? <img src={p.pic} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                        : <div style={{ width: 40, height: 40, borderRadius: 8, background: '#e2e8f0', flexShrink: 0 }} />}
                      <div style={{ minWidth: 0 }}>
                        <div><strong>{p.firstName}</strong>{p.age ? `, ${p.age}` : ''}<ShortId userId={p.userId} /></div>
                        <div className="as-card-meta">{p.localArea || 'No area'}</div>
                        <div className="as-card-meta">
                          Them: {p.memberAnswer || 'no answer'} · {p.firstName}: {p.theirAnswer || 'no answer'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MemberInfoPanel = ({ member, communityId, communityTitle, authedFetch, onClose, focusBotChat = false }) => {
  const m = member;
  const profile = m.communityProfile || {};
  const otherCommunities = (m.joinedCommunities || []).map((c) => c.communityTitle).filter((t) => t && t !== communityTitle);

  return (
    <div className="as-confirm-overlay" onClick={onClose}>
      <div className="as-confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px', maxHeight: '80vh', overflowY: 'auto', textAlign: 'left' }}>
        <h3 style={{ marginTop: 0 }}>{m.firstName || '(no name)'}<ShortId userId={m.userId} /></h3>
        <div className="as-card-meta" style={{ marginTop: '-0.6rem', marginBottom: '0.6rem' }}>userId {m.userId}</div>

        {m.pics?.length > 0 && (
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
            {m.pics.map((src) => <img key={src} src={src} alt="" style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 8 }} />)}
          </div>
        )}

        <Row label="Age" value={m.age} />
        <Row label="Gender" value={m.gender} />
        <Row label="Area" value={m.localArea || 'Not set'} />
        <Row label="Map position" value={m.coordinates ? `${m.coordinates[1].toFixed(4)}, ${m.coordinates[0].toFixed(4)}` : 'Not set'} />
        <Row label={`Joined ${communityTitle || 'this community'}`} value={fmtDate(m.joinedAt)} />
        <Row label="Status" value={m.active ? 'Active' : 'Paused'} />
        <Row label="Also in" value={otherCommunities} />

        <h4 style={{ margin: '0.9rem 0 0.3rem' }}>{communityTitle || 'Community'} profile</h4>
        {!Object.values(profile).some((v) => show(v).trim()) && <div className="as-card-meta">Nothing filled in.</div>}
        {Object.entries(profile).map(([k, v]) => <Row key={k} label={k} value={v} />)}
        <Row label="Who they want to meet" value={m.seeking} />
        <Row label="Bot's notes" value={m.botNotes} />
        <Row label="Only suggest people within" value={m.maxDistanceMiles ? `${m.maxDistanceMiles} miles` : ''} />

        <BotChat communityId={communityId} userId={m.userId} authedFetch={authedFetch} scrollIntoView={focusBotChat} />

        <SuggestionActivity communityId={communityId} userId={m.userId} authedFetch={authedFetch} memberName={m.firstName || 'They'} />

        <h4 style={{ margin: '0.9rem 0 0.3rem' }}>/meet AI chat</h4>
        {(m.meetChats || []).length === 0
          ? <div className="as-card-meta">Didn&apos;t come through the /meet chat.</div>
          : m.meetChats.map((c) => <MeetChat key={c.sessionId} chat={c} authedFetch={authedFetch} />)}

        <div style={{ marginTop: '1rem', textAlign: 'right' }}>
          <button type="button" className="as-filter-tab" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default MemberInfoPanel;
